import { useCallback, useEffect, useState } from 'react';
import {
  useAppState,
  useConfirmation,
  useQuote,
  useUrlParams,
  store,
  useGaEventData,
} from '../../store/store';

import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ConfirmationListItem } from './ConfirmationPage.types';
import { formatPurchasedPlan, formatRegistrationData } from './utils/formatConfirmationPanels';
import { ButtonLink } from '@domgen/base-design-system-2.0/atoms/ButtonLink';
import { Copy } from '../../assets/icons/Copy';
import { cpgImages } from './constants/cpgImages';
import { usePostHog } from '@posthog/react';
import { gaPageView, gaECommerceEvent, gaEvent } from '../../services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { getSystemCode } from '../../utils/getSystemCode';
import { ConfirmationUpsellPanel } from './components/ConfirmationUpsellPanel';
import { getDiscountDisplayData } from '../../utils/discounts/getDiscountDisplayData';
import { QuoteResponse } from '../../models/services/api/postQuote';
import { StoreEvent } from '../../store/store.types';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { capturePostHogEvent } from '../../services/posthog/capture';

const styles = {
  container:
    'grid grid-cols-12 gap-4 items-center p-(--confirmation-page-panel-padding) bg-(--confirmation-page-background)',
  panel:
    'col-span-12 w-2xl h-auto p-5 rounded-(--border-radius) bg-(--confirmation-page-panel-background) m-2 self-center',
  descriptionList: 'flex flex-col gap-2 mt-4 w-full',
  descriptionListItemTitle: 'text-body-small-strong mb-space-100',
  descriptionListItem:
    'w-full text-body-small inline-flex justify-start items-center gap-1 mb-2 bg-(--confirmation-page-item-background) border border-inset border-(--confirmation-page-item-border-color) rounded-(--confirmation-page-item-border-radius) px-(--confirmation-page-item-padding-x) py-(--confirmation-page-item-padding-y)',
  article:
    'col-span-6 rounded-(--confirmation-page-panel-border-radius) bg-(--confirmation-page-panel-background) p-(--confirmation-page-panel-padding) w-full h-fit',
  cpgPanel: 'flex flex-col justify-center items-center gap-2 text-center',
};

export function ConfirmationPage() {
  const {
    VITE_CLIENT_FRIENDLY_NAME,
    VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH,
    VITE_BRAND_CODE,
  } = import.meta.env;
  const confirmation = useConfirmation();
  const { cpgData, conciergeData } = confirmation ?? {};
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [isCopied, setIsCopied] = useState(false);
  const posthog = usePostHog();
  const registerOnly = confirmation?.regOnlyData ?? false;
  const quote = useQuote()?.data as QuoteResponse | undefined;
  const discountTerms = quote && quote?.offer ? getDiscountDisplayData(quote) : undefined;

  const { applianceName, categoryName, modelNumber, serialNumber, arrivalDate, purchasePrice } =
    confirmation?.applianceDetails ?? {};

  useEffect(() => {
    if (confirmation?.regOnlyData) {
      capturePostHogEvent(posthog, 'registration-only-order-confirmation-loaded', store.get());
    } else {
      capturePostHogEvent(posthog, 'registration-upsell-order-confirmation-loaded', store.get());
    }
  }, []);

  const { discountCode, startDate, netFirstPaymentAmount, itemId } = confirmation?.offerData ?? {};
  const gaEventData = useGaEventData();
  const salesWidgetClicked = gaEventData?.salesWidgetClicked;

  const {
    discountTermFromPurchase,
    discountCodeFromPurchase,
    mappedPaymentType,
    transactionId,
    planNumber,
    netFirstPaymentAmountFromPurchase,
    netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
  } = confirmation?.purchaseData ?? {};

  const appSessionID = session?.id;

  const formattedArrivalDate = arrivalDate
    ? formatLocalisedDate(parseLocalDateString(arrivalDate), 'en-US')
    : '';
  const formattedStartDate = startDate
    ? formatLocalisedDate(parseLocalDateString(startDate), 'en-US')
    : '';

  const registration = formatRegistrationData(
    VITE_CLIENT_FRIENDLY_NAME,
    applianceName,
    modelNumber,
    serialNumber,
    formattedArrivalDate,
    /**
     * For appliances where there is no matching model against the model number we won't know the guarantee length so we
     * will default to 12 months.
     */
    confirmation?.applianceDetails?.guarantee ?? 12,
  );

  const purchasePlan = confirmation?.purchaseData
    ? formatPurchasedPlan(
        VITE_CLIENT_FRIENDLY_NAME,
        applianceName,
        formattedStartDate,
        planNumber,
        netFirstPaymentAmountFromPurchase,
        netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
        discountTermFromPurchase,
      )
    : null;

  const offerStatus = confirmation?.offerData ? '1' : '0';

  useEffect(() => {
    if (confirmation?.regOnlyData) registerOnlyGaEvents();
    else purchasePriceGaEvents();
  }, []);

  const applianceCareTerms =
    "Applicable taxes calculated monthly. Your discounted rate is shown above. Your plan documents (which we've emailed to you) contain the full breakdown of your payment plus any applicable taxes. Charges will appear on your billing statement as 'D&G Svc Plan'. Appliance Care Plan Terms & Conditions";

  const registerOnlyGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      offerStatus,
      salesWidgetClicked,
    });
    gaEvent({
      event: 'registrationStored',
      eventLabel: '1',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      registrationStored: '1',
      offerStatus,
      salesWidgetClicked,
    });

    if (discountTerms && registerOnly) {
      gaEvent({
        eventCategory: 'appliance details form USA',
        eventAction: 'Reg Confirmation Sales Widget',
        eventLabel: 'Offer Presented',
        pagename: 'confirmation',
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceName,
        applianceCategory: categoryName,
        appliancePurchaseCost: purchasePrice,
        appliancePurchaseDate: formattedArrivalDate,
        modelNumberDetail: modelNumber,
        serialNumberDetail: serialNumber,
        serialNumber: serialNumber ? 'provided' : 'not provided',
        modelNumber: modelNumber ? 'provided' : 'not provided',
        offerPrice: netFirstPaymentAmount,
        appSessionID: session?.id,
        promotionApplied: discountCodeFromPurchase ?? discountCode,
      });
    }
  };

  const purchasePriceGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      paymentType: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
      planNumber,
      salesWidgetClicked,
    });

    gaECommerceEvent({
      pagename: 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'purchase',
      regSystemCode: systemCode,
      version: 'WiB',
      domainId: VITE_CLIENT_FRIENDLY_NAME.replaceAll(' ', '').toUpperCase(),
      brand: VITE_BRAND_CODE.trim(),
      country: 'USA',
      customerType: 'G',
      offerStatus,
      item_promotion_name: discountCodeFromPurchase ?? discountCode,
      appSessionID,
      applianceName,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      applianceCategory: categoryName,
      salesWidgetClicked,
      ecommerce: {
        currency: 'USD',
        value: netFirstPaymentAmountFromPurchase,
        transaction_id: transactionId,
        items: [
          {
            appliance_purchase_cost: Number(purchasePrice),
            appliance_purchase_date: formattedArrivalDate, // USA format date
            applianceName,
            appliance_warranty: true,
            item_brand: VITE_CLIENT_FRIENDLY_NAME,
            item_category: categoryName,
            item_id: itemId,
            item_name: `${VITE_CLIENT_FRIENDLY_NAME} ${applianceName}`,
            item_promotion_name: discountCodeFromPurchase ?? discountCode,
            modelNumberDetail: modelNumber,
            paymentType: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
            price: netFirstPaymentAmountFromPurchase,
            quantity: 1,
            regSystemCode: systemCode,
            serialNumberDetail: serialNumber,
          },
        ],
      },
    });
  };

  const header = () =>
    registerOnly
      ? {
          title: 'That’s it, you’re all set!',
          body: 'Registration confirmed. We’ll send you a confirmation email shortly.',
        }
      : {
          title: "That's it, you’re all set!",
          body: 'Registration and protection confirmed. We’ll send you a confirmation email shortly.',
        };

  const copy = () => {
    if (cpgData) {
      navigator.clipboard.writeText(cpgData.promoCode);
      setIsCopied(true);
    }
  };

  const cpgImage = () => {
    if (!cpgData) {
      return;
    }
    return cpgImages.find((image: Record<string, string>) => image.id === cpgData.filter);
  };

  const concierge = conciergeData && (
    <>
      <p className="my-4 flex justify-center">{conciergeData?.header_text}</p>
      <ul className="list-disc list-inside gap-space-300 mb-space-500 flex flex-col ">
        {conciergeData?.bullet_points.map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
      <div className="flex justify-center my-4">
        <div className="w-full md:w-fit">
          <ButtonLink href={conciergeData?.url}>
            <span className="uppercase">{conciergeData?.button_text}</span>
          </ButtonLink>
        </div>
      </div>
    </>
  );

  const applianceCare = (
    <div className="flex flex-col text-left">
      <p className="my-4">{applianceCareTerms}</p>
      <TextLink
        href={VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
        target="_blank"
        rel="noopener noreferrer"
      >
        Appliance Care Terms & Conditions
      </TextLink>
    </div>
  );

  const listItem = (listItem: ConfirmationListItem) => (
    <div key={`${listItem.label}-${listItem.value}`}>
      <dt className={styles.descriptionListItemTitle}>{listItem.label}</dt>
      <dd className={styles.descriptionListItem}>
        <span className="mr-2 text-(--color-icon-list-decorative)">
          {typeof listItem.icon === 'string' ? (
            <img src={listItem.icon} alt={listItem.label} height={16} width={16} />
          ) : (
            listItem.icon
          )}
        </span>
        {listItem.value}
      </dd>
    </div>
  );

  const upsellBanner = () => (
    <div className="bg-button-bg-primary-default  text-button-text-primary-default w-full text-center py-space-200 px-space-600">
      <p className="text-body-large-strong">
        Get a discounted price on appliance care today - limited time only!
      </p>
    </div>
  );

  const handleCheckoutNavigation = useCallback(() => {
    capturePostHogEvent(posthog, 'confirmation-upsell-clicked', store.get());

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'Reg Confirmation Sales Widget ',
      eventLabel: 'CTA Clicked',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      offerPrice: netFirstPaymentAmount,
      appSessionID: session?.id,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
    });

    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: false },
    });
  }, []);

  return (
    <>
      {discountTerms && registerOnly && upsellBanner()}
      <div className={styles.container}>
        <header className="col-span-12 text-center">
          <h1 className="text-h3 mb-(--confirmation-page-header-title-spacing)">
            {header().title}
          </h1>
          <p className="mb-(--confirmation-page-header-body-spacing)">{header().body}</p>
        </header>

        <div className="col-span-12 flex justify-center ">
          <div className="w-full max-w-178 grid grid-cols-1 md:grid-cols-6 gap-(--confirmation-page-panel-gap)">
            {registration && (
              <article
                className={`${!registerOnly || cpgData ? 'md:col-span-3' : ''}  ${styles.article}`}
              >
                <h2 className="text-body-large-strong">{registration.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {registration.data.map((item) => listItem(item))}
                </dl>
                {conciergeData && concierge}
              </article>
            )}

            {purchasePlan && !registerOnly && (
              <article className={`${styles.article} md:col-span-3`}>
                <h2 className="text-body-large-strong">{purchasePlan.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {purchasePlan.data.map((item) => listItem(item))}
                </dl>
                {applianceCare}
              </article>
            )}

            {cpgData && cpgImage() && (
              <article
                className={`${!registerOnly && purchasePlan && cpgData ? 'col-span-6 md:col-span-3 md:col-start-2' : registerOnly ? 'col-span-6 md:col-span-3 md:col-start-4' : ''} col-span-6 md:col-span-3 ${styles.article} ${styles.cpgPanel} `}
              >
                <h6 className="text-h6 leading-space-700">{cpgData.title}</h6>
                <img
                  className="max-w-[173px] max-h-[173px]"
                  src={`${cpgImage()?.image}`}
                  alt={cpgData.productTitle}
                />
                <p className="text-body-strong"> {cpgData.productTitle}</p>
                <p>{cpgData.description}</p>
                <TextLink onClick={copy} leftIcon={<Copy />}>
                  {isCopied ? 'Copied!' : 'Copy this code'}
                </TextLink>

                <ButtonLink href={cpgData.waterFilterUrl} variant="primary">
                  <span className="uppercase">{cpgData.cta}</span>
                </ButtonLink>
              </article>
            )}
          </div>
        </div>
      </div>

      <div className="bg-(--confirmation-page-background) w-full flex justify-center py-space-500">
        {registerOnly && discountTerms && applianceName && (
          <ConfirmationUpsellPanel
            applianceName={applianceName}
            netFirstPaymentAmountFromPurchase={discountTerms.netFirstPaymentAmount}
            discountPaymentType={discountTerms.discountPaymentType}
            discountTerm={discountTerms.discountTerm}
            discountPercentage={discountTerms.discountPercentage}
            handleCheckoutNavigation={handleCheckoutNavigation}
          />
        )}
      </div>
    </>
  );
}
